// ─────────────────────────────────────────────────────────────────────────────
// 🔑 "WITHDRAW FILE" — pull a loan file before the borrower registers (broker gaps, Jesse 2026-11-02).
//
// Rendered by My Borrowers (the submitter's own files) and Team Pipeline (the
// broker of record, a member's files) ONLY when the server says the switch is on
// (withdraw_enabled — backend constants/brokerGapsFlags PARTNER_WITHDRAW_SUBMISSION,
// on by default; 'off' is the kill switch): wording approved by Hannah 2026-11-03
// (.scratch/reports/morning-decisions.md, "Broker gaps — for Hannah").
//
// ⚠️ THE SAME CONFIRM PATTERN AS "Resend invite": the link opens BorrowerConsentCell's
// Modal (Escape and the backdrop close it), one primary button, a plain Cancel, and
// the server's own refusal shown in the modal. The server decides who may withdraw
// and when; this control only asks.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useState } from 'react';
import { Modal } from './BorrowerConsentCell';

const API_URL = process.env.REACT_APP_API_URL || '';

const LINK = {
  border: 'none', background: 'none', padding: 0, color: '#b3321f',
  fontSize: 12, fontWeight: 700, cursor: 'pointer', textDecoration: 'underline', whiteSpace: 'nowrap',
};

export default function WithdrawFileControl({ token, submissionId, borrowerName, onWithdrawn }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const withdraw = useCallback(() => {
    setBusy(true);
    setMsg(null);
    fetch(`${API_URL}/api/doorlypro/submissions/${submissionId}/withdraw`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    })
      .then((r) => r.json().then((d) => ({ status: r.status, d })))
      .then(({ status, d }) => {
        if (status === 200 && d.ok) {
          setOpen(false);
          if (typeof onWithdrawn === 'function') onWithdrawn();
        } else {
          setMsg(d.error || 'Could not withdraw that file.');
        }
      })
      .catch(() => setMsg('Network error — nothing was changed.'))
      .finally(() => setBusy(false));
  }, [submissionId, token, onWithdrawn]);

  return (
    <>
      <button type="button" data-testid="withdraw-file" onClick={() => { setMsg(null); setOpen(true); }} style={LINK}>
        Withdraw file
      </button>
      {open && (
        <Modal title="Withdraw this loan file" subtitle={borrowerName ? `For ${borrowerName}.` : null}
          onClose={() => { setOpen(false); setMsg(null); }}>
          <p style={{ margin: 0, fontSize: 13, color: '#4b5563', lineHeight: 1.6 }}>
            Your borrower has not registered yet. Withdrawing stops this file: the link in their invite stops
            working and Doorly will not contact them about it. This can&rsquo;t be undone &mdash; to go ahead later,
            submit the file again.
          </p>
          <div style={{ marginTop: 14, display: 'flex', gap: 10, alignItems: 'center' }}>
            <button type="button" data-testid="withdraw-file-confirm" onClick={withdraw} disabled={busy}
              style={{ border: 'none', background: '#b3321f', color: '#fff', borderRadius: 8, padding: '9px 16px',
                fontSize: 13, fontWeight: 700, cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.6 : 1 }}>
              {busy ? 'Withdrawing…' : 'Withdraw it'}
            </button>
            <button type="button" onClick={() => { setOpen(false); setMsg(null); }}
              style={{ ...LINK, textDecoration: 'none', color: '#6b7280' }}>Cancel</button>
          </div>
          {msg && (
            <p style={{ margin: '12px 0 0', fontSize: 12.5, lineHeight: 1.6, fontWeight: 600, color: '#b3321f' }}>{msg}</p>
          )}
        </Modal>
      )}
    </>
  );
}
